import { useMutation } from "@apollo/client";
import { z } from "zod";
import { Body2, Checkbox, ExternalLink, FieldError, XStack, YStack } from "@medbillai/ui";
import { type OnboardingNavProps, OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { type BoundFieldMeta, FormIsoDateField, FormTextField, SUBMIT_ERROR_MESSAGE, useFormEngine, userDateOfBirthSchema, visibleFieldError } from "../../lib/form-engine";
import type { OnboardingIdentity } from "./types";
import { recordIdentityMutation } from "./utils/queries";
import { useAcceptTerms } from "./utils/useAcceptTerms";
export type ProfileOutcomes = {
  submitted: OnboardingIdentity;
};
const Outcome = tagged<ProfileOutcomes>();
const TERMS_URL = "https://grantedhealth.com/granted/legal/terms";
const PRIVACY_URL = "https://grantedhealth.com/granted/legal/privacy";
export const PROFILE_SUBTITLE = "Please provide these details as they appear on your government ID.";
const profileSchema = z.object({
  firstName: z.string().trim().min(1, "Required"),
  lastName: z.string().trim().min(1, "Required"),
  // ISO YYYY-MM-DD; the masked date field commits text that parses to no
  // calendar date raw, so the format refine is what rejects it here.
  dateOfBirth: userDateOfBirthSchema(),
  termsAccepted: z.boolean().refine(accepted => accepted, "Please accept the terms to continue")
});

// Every field has an in-domain empty value ("", false), so the schema's input
// type is the draft type as-is.
type ProfileFormValues = z.input<typeof profileSchema>;
export function TermsCheckbox({
  checked,
  meta,
  onCheckedChange
}: {
  checked: boolean;
  meta?: BoundFieldMeta;
  onCheckedChange: (checked: boolean) => void;
}) {
  return <YStack gap="$sp1" data-sentry-element="YStack" data-sentry-component="TermsCheckbox" data-sentry-source-file="Profile.tsx">
      <XStack gap="$sp1.5" alignItems="center" data-sentry-element="XStack" data-sentry-source-file="Profile.tsx">
        <Checkbox testID="complete-profile.terms" tone="brand" checked={checked} onCheckedChange={value => onCheckedChange(value === true)} aria-label="Accept the Terms of Use and Privacy Policy" data-sentry-element="Checkbox" data-sentry-source-file="Profile.tsx" />
        <Body2 color="$textSubtle" flex={1} data-sentry-element="Body2" data-sentry-source-file="Profile.tsx">
          By signing up you agree to the{" "}
          <ExternalLink href={TERMS_URL} title="Terms of Use" color="$textDefault" textDecorationLine="underline" target="_blank" rel="noreferrer" data-sentry-element="ExternalLink" data-sentry-source-file="Profile.tsx" />{" "}
          and acknowledge the{" "}
          <ExternalLink href={PRIVACY_URL} title="Privacy Policy" color="$textDefault" textDecorationLine="underline" target="_blank" rel="noreferrer" data-sentry-element="ExternalLink" data-sentry-source-file="Profile.tsx" />
          .
        </Body2>
      </XStack>
      <FieldError message={meta ? visibleFieldError(meta) : undefined} data-sentry-element="FieldError" data-sentry-source-file="Profile.tsx" />
    </YStack>;
}
export function Profile({
  onDone,
  initial,
  termsAccepted = false,
  nav
}: {
  onDone: OnDone<ProfileOutcomes>;
  /** Prefills the form, e.g. to check details already on file. */
  initial?: OnboardingIdentity;
  /** The user accepted the terms earlier: no checkbox, no re-acceptance. */
  termsAccepted?: boolean;
  nav?: OnboardingNavProps;
}) {
  const [recordIdentity] = useMutation(recordIdentityMutation);
  const {
    requestAcceptance
  } = useAcceptTerms();
  const {
    form,
    formError,
    submit
  } = useFormEngine<ProfileFormValues, z.output<typeof profileSchema>>({
    defaultValues: {
      firstName: initial?.firstName ?? "",
      lastName: initial?.lastName ?? "",
      dateOfBirth: initial?.dateOfBirth ?? "",
      termsAccepted
    },
    schema: profileSchema,
    onSubmit: async values => {
      // Acceptance is deliberately not tied to the checkbox press: a stamp
      // written before the identity call reads as "past the terms step"
      // everywhere downstream, so a user who checks the box and leaves would
      // never be resumed. recordIdentity is server-gated on the stamp, so it
      // still has to land first — just within the same submit.
      const accepted = termsAccepted || (await requestAcceptance());
      if (!accepted) {
        // The hook already reported the failure; this is the expected-failure
        // channel, so the user gets the retryable form error.
        return {
          status: "invalid",
          formError: SUBMIT_ERROR_MESSAGE
        };
      }
      // Throws bubble to the engine: Sentry capture + the generic form error.
      // `values` is the schema's parsed output — names arrive already trimmed.
      await recordIdentity({
        variables: {
          input: {
            firstName: values.firstName,
            lastName: values.lastName,
            dateOfBirth: values.dateOfBirth
          }
        }
      });
      return {
        status: "ok"
      };
    },
    onSubmitSuccess: values => {
      onDone(Outcome.submitted({
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: values.dateOfBirth
      }));
    }
  });
  return <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-component="Profile" data-sentry-source-file="Profile.tsx">
      <OnboardingScreen.Nav {...nav} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Profile.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Profile.tsx">
        <OnboardingScreen.Header alignment="left" title="Complete your profile" subtitle={PROFILE_SUBTITLE} data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Profile.tsx" />
        <YStack gap="$sp2" data-sentry-element="YStack" data-sentry-source-file="Profile.tsx">
          <form.Field name="firstName" data-sentry-element="form.Field" data-sentry-source-file="Profile.tsx">
            {field => <FormTextField field={field} required testID="complete-profile.first-name" label="First name" autoCapitalize="words" autoComplete="given-name" autoCorrect={false} spellCheck={false} />}
          </form.Field>
          <form.Field name="lastName" data-sentry-element="form.Field" data-sentry-source-file="Profile.tsx">
            {field => <FormTextField field={field} required testID="complete-profile.last-name" label="Last name" autoCapitalize="words" autoComplete="family-name" autoCorrect={false} spellCheck={false} />}
          </form.Field>
          <form.Field name="dateOfBirth" data-sentry-element="form.Field" data-sentry-source-file="Profile.tsx">
            {field => <FormIsoDateField field={field} required testID="complete-profile.dob" label="Date of birth" />}
          </form.Field>
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Profile.tsx">
        {termsAccepted ? null : <form.Field name="termsAccepted">
            {field => <TermsCheckbox checked={field.state.value} meta={field.state.meta} onCheckedChange={checked => field.handleChange(checked)} />}
          </form.Field>}
        <OnboardingScreen.FormError message={formError} data-sentry-element="OnboardingScreen.FormError" data-sentry-source-file="Profile.tsx" />
        <form.Subscribe selector={state => [state.isSubmitting, state.values.termsAccepted] as const} data-sentry-element="form.Subscribe" data-sentry-source-file="Profile.tsx">
          {([isSubmitting, termsAccepted]) => <OnboardingScreen.PrimaryAction testID="complete-profile.submit" variant="brand"
        // Gated on the checkbox (not full validity): an unchecked-terms
        // submit would only pop the error below the button and shift
        // the layout. Name/DOB gaps still surface on tap — submit(),
        // not form.handleSubmit, so a failed attempt's carried error
        // can't silently block the retry.
        disabled={isSubmitting || !termsAccepted} loading={isSubmitting} onPress={() => submit()}>
              Continue
            </OnboardingScreen.PrimaryAction>}
        </form.Subscribe>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}